import { cx } from 'linaria';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import * as React from 'react';
import { render } from 'react-dom';
import * as styles from './data-grid.styles';
import { useScreenClass } from '../grid';
import { IconArrowDown, IconArrowUp } from '../foundation/icons';

export type DataGridProps<TData> = AgGridReactProps<TData> & {
    testId?: string;
    enableRowHoverHighlight?: boolean;
    dense?: boolean;
};
const getDomNodeForElement = (icon: React.ReactElement): Element => {
    const domNode = document.createElement('div');
    render(icon, domNode);
    return domNode;
};
const ROW_HEIGHT_DENSE = 48;
const ROW_HEIGHT_NORMAL = 68;

function DataGridInner<TData>(props: DataGridProps<TData>, ref: React.MutableRefObject<AgGridReact<TData>>) {
    const screenClass = useScreenClass();

    // Row hover highlight disabled by default
    const { enableRowHoverHighlight = false, columnTypes = {}, dense, ...passThroughProps } = props;

    const isDense = React.useMemo(() => screenClass === 'sm' || dense, [screenClass, dense]);

    const defaultColDef = React.useMemo(
        () => ({
            ...(passThroughProps.defaultColDef || {}),
            icons: {
                sortAscending: () => getDomNodeForElement(<IconArrowUp />),
                sortDescending: () => getDomNodeForElement(<IconArrowDown />),
                ...(passThroughProps.defaultColDef?.icons || {}),
            },
        }),
        [passThroughProps.defaultColDef],
    );

    return (
        <AgGridReact
            ref={ref}
            className={cx(styles['root'], 'ag-theme-alpine', dense && styles['dense'])}
            containerStyle={{
                '--ag-grid-size': isDense ? `${ROW_HEIGHT_DENSE / 7}px` : `${ROW_HEIGHT_NORMAL / 7}px`,
            }}
            rowHeight={isDense ? ROW_HEIGHT_DENSE : ROW_HEIGHT_NORMAL}
            suppressRowHoverHighlight={!enableRowHoverHighlight}
            defaultColDef={defaultColDef}
            columnTypes={{
                ...columnTypes,
                iconColumn: {
                    headerClass: styles['centredColumnHeader'],
                    cellClass: styles['centredColumnCell'],
                },
                dropdownColumn: {
                    headerClass: styles['centredColumnHeader'],
                    cellClass: styles['dropdownColumnCell'],
                },
            }}
            accentedSort
            {...passThroughProps}
        />
    );
}
export const DataGrid = React.forwardRef(DataGridInner) as <TData>(
    props: DataGridProps<TData> & { ref?: React.MutableRefObject<AgGridReact<TData>> },
) => ReturnType<typeof DataGridInner>;
